import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { CircleNotch, Tag, Plus, X, PencilSimple, Archive, ArrowCounterClockwise } from '@phosphor-icons/react';
import { platformApi, getPlatformApiErrorMessage } from '@/lib/platformApi';
import { toast } from 'sonner';
import { ScrollLock } from '@/components/shared/ScrollLock';

// ── Types ─────────────────────────────────────────────────────────────────────

interface PlanPrice {
  id: string;
  key: string;
  name: string;
  monthlyPrice: string;        // Prisma Decimal serialises as a string
  annualPrice: string | null;
  currency: string;
  trialDays: number;
  status: string;              // active | archived
  sortOrder: number;
}

// ── Helpers ───────────────────────────────────────────────────────────────────

const gbp = (v: string | number | null | undefined) =>
  v == null ? '—' : new Intl.NumberFormat('en-GB', { style: 'currency', currency: 'GBP' }).format(Number(v));

function StatusBadge({ status }: { status: string }) {
  const active = status === 'active';
  return (
    <span className={`inline-flex items-center rounded-md px-2 py-0.5 text-xs font-medium capitalize ${active ? 'bg-plat-success-soft text-plat-success-text' : 'bg-plat-neutral-soft text-plat-muted'}`}>
      {status}
    </span>
  );
}

// ── Create / edit modal ─────────────────────────────────────────────────────────

function PlanModal({ plan, onClose, onSaved }: { plan: PlanPrice | null; onClose: () => void; onSaved: () => void }) {
  const isEdit = !!plan;
  const [key, setKey] = useState(plan?.key ?? '');
  const [name, setName] = useState(plan?.name ?? '');
  const [monthly, setMonthly] = useState(plan ? String(Number(plan.monthlyPrice)) : '');
  const [annual, setAnnual] = useState(plan?.annualPrice != null ? String(Number(plan.annualPrice)) : '');
  const [trialDays, setTrialDays] = useState(String(plan?.trialDays ?? 14));

  const mutation = useMutation({
    mutationFn: () => {
      const body = {
        name: name.trim(),
        monthlyPrice: Number(monthly || 0),
        annualPrice: annual.trim() === '' ? null : Number(annual),
        trialDays: Number(trialDays || 0),
      };
      return isEdit
        ? platformApi.patch(`/plan-prices/${plan!.id}`, body)
        : platformApi.post('/plan-prices', { key: key.trim().toLowerCase(), ...body });
    },
    onSuccess: () => { toast.success(isEdit ? 'Plan updated' : 'Plan created'); onSaved(); onClose(); },
    onError: (err) => toast.error(getPlatformApiErrorMessage(err)),
  });

  const inputCls = 'h-9 w-full rounded-lg border border-plat-border bg-plat-bg px-3 text-sm text-plat-fg placeholder-plat-faint focus:border-plat-focus focus:outline-none';
  const canSave = name.trim() && (isEdit || /^[a-z0-9_]+$/.test(key.trim().toLowerCase()));

  return (
    <div className="fixed inset-0 z-50 flex items-start justify-center overflow-y-auto bg-plat-scrim/60 px-4">
      <ScrollLock />
      <div className="my-auto w-full max-w-md rounded-xl border border-plat-border bg-plat-surface p-6 shadow-2xl">
        <div className="mb-5 flex items-center justify-between">
          <h3 className="text-[15px] font-semibold text-plat-fg">{isEdit ? `Edit ${plan!.name}` : 'New plan'}</h3>
          <button onClick={onClose} className="text-plat-muted hover:text-plat-secondary"><X className="h-4 w-4" /></button>
        </div>
        <div className="space-y-4">
          {!isEdit && (
            <div>
              <label className="mb-1.5 block text-xs font-medium text-plat-secondary">Key <span className="text-plat-faint">(lowercase id, e.g. pro)</span></label>
              <input value={key} onChange={(e) => setKey(e.target.value)} placeholder="pro" className={inputCls} />
            </div>
          )}
          <div>
            <label className="mb-1.5 block text-xs font-medium text-plat-secondary">Name</label>
            <input value={name} onChange={(e) => setName(e.target.value)} placeholder="Professional" className={inputCls} />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="mb-1.5 block text-xs font-medium text-plat-secondary">Monthly price (£)</label>
              <input type="number" min="0" step="0.01" value={monthly} onChange={(e) => setMonthly(e.target.value)} placeholder="0.00" className={inputCls} />
            </div>
            <div>
              <label className="mb-1.5 block text-xs font-medium text-plat-secondary">Annual price (£)</label>
              <input type="number" min="0" step="0.01" value={annual} onChange={(e) => setAnnual(e.target.value)} placeholder="Custom" className={inputCls} />
            </div>
          </div>
          <div>
            <label className="mb-1.5 block text-xs font-medium text-plat-secondary">Trial length (days)</label>
            <input type="number" min="0" step="1" value={trialDays} onChange={(e) => setTrialDays(e.target.value)} className={inputCls} />
          </div>
          <div className="flex justify-end gap-2 pt-1">
            <button onClick={onClose} className="rounded-lg border border-plat-border px-4 py-2 text-sm text-plat-secondary hover:bg-plat-hover">Cancel</button>
            <button
              onClick={() => mutation.mutate()}
              disabled={!canSave || mutation.isPending}
              className="inline-flex items-center gap-1.5 rounded-lg bg-plat-accent-solid px-4 py-2 text-sm font-medium text-plat-on-accent hover:bg-plat-accent-solid-hover disabled:bg-plat-neutral-soft disabled:cursor-not-allowed disabled:text-plat-disabled"
            >
              {mutation.isPending ? <CircleNotch className="h-4 w-4 animate-spin" /> : isEdit ? 'Save changes' : 'Create plan'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

// ── Page ──────────────────────────────────────────────────────────────────────

export default function PlansPricingPage() {
  const queryClient = useQueryClient();
  const [editing, setEditing] = useState<PlanPrice | null | 'new'>(null);

  const { data, isLoading, error } = useQuery<PlanPrice[]>({
    queryKey: ['platform', 'plan-prices'],
    queryFn: async () => {
      const res = await platformApi.get('/plan-prices');
      return (res.data?.data ?? res.data) as PlanPrice[];
    },
  });

  const statusMutation = useMutation({
    mutationFn: ({ id, action }: { id: string; action: 'archive' | 'activate' }) =>
      platformApi.post(`/plan-prices/${id}/${action}`, {}),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['platform', 'plan-prices'] });
      toast.success('Plan updated');
    },
    onError: (err) => toast.error(getPlatformApiErrorMessage(err)),
  });

  const invalidate = () => queryClient.invalidateQueries({ queryKey: ['platform', 'plan-prices'] });
  const plans = data ?? [];

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold text-plat-fg">Plans & Pricing</h2>
          <p className="mt-0.5 text-sm text-plat-muted">Your product catalogue — set the prices that every subscription and invoice is calculated from.</p>
        </div>
        <button
          onClick={() => setEditing('new')}
          className="inline-flex items-center gap-2 rounded-lg bg-plat-accent-solid px-4 py-2 text-sm font-medium text-plat-on-accent hover:bg-plat-accent-solid-hover"
        >
          <Plus className="h-4 w-4" /> New plan
        </button>
      </div>

      {isLoading ? (
        <div className="flex items-center justify-center py-16"><CircleNotch className="h-7 w-7 animate-spin text-plat-info-text" /></div>
      ) : error ? (
        <div className="rounded-xl border border-plat-danger-line bg-plat-danger-soft p-5 text-sm text-plat-danger-text">{getPlatformApiErrorMessage(error)}</div>
      ) : (
        <div className="overflow-hidden rounded-xl border border-plat-border bg-plat-surface">
          <table className="w-full text-left text-sm">
            <thead>
              <tr className="border-b border-plat-border">
                <th className="px-5 py-3.5 text-xs font-semibold uppercase tracking-[0.06em] text-plat-muted">Plan</th>
                <th className="px-5 py-3.5 text-right text-xs font-semibold uppercase tracking-[0.06em] text-plat-muted">Monthly</th>
                <th className="px-5 py-3.5 text-right text-xs font-semibold uppercase tracking-[0.06em] text-plat-muted">Annual</th>
                <th className="px-5 py-3.5 text-right text-xs font-semibold uppercase tracking-[0.06em] text-plat-muted">Trial</th>
                <th className="px-5 py-3.5 text-xs font-semibold uppercase tracking-[0.06em] text-plat-muted">Status</th>
                <th className="px-5 py-3.5 text-right text-xs font-semibold uppercase tracking-[0.06em] text-plat-muted">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-plat-border">
              {plans.length === 0 ? (
                <tr><td colSpan={6} className="py-10 text-center text-sm text-plat-muted">No plans yet.</td></tr>
              ) : plans.map((p) => (
                // An archived row reads as inactive through its SURFACE, not by dimming its data:
                // `opacity-60` faded the prices and the status badge along with everything else,
                // and the row still has to be readable to be audited.
                <tr key={p.id} className={p.status === 'archived' ? 'bg-plat-sunken' : 'hover:bg-plat-hover'}>
                  <td className="px-5 py-3.5">
                    <div className="font-medium text-plat-fg">{p.name}</div>
                    <div className="text-[10px] text-plat-faint">{p.key}</div>
                  </td>
                  <td className="px-5 py-3.5 text-right font-medium text-plat-neutral-text">{gbp(p.monthlyPrice)}</td>
                  <td className="px-5 py-3.5 text-right text-plat-secondary">{p.annualPrice == null ? <span className="text-plat-faint">Custom</span> : gbp(p.annualPrice)}</td>
                  <td className="px-5 py-3.5 text-right text-plat-secondary">{p.trialDays} days</td>
                  <td className="px-5 py-3.5"><StatusBadge status={p.status} /></td>
                  <td className="px-5 py-3.5 text-right">
                    <div className="flex items-center justify-end gap-2">
                      <button onClick={() => setEditing(p)} className="inline-flex items-center gap-1 rounded-md border border-plat-border px-2 py-1 text-xs text-plat-secondary hover:bg-plat-hover">
                        <PencilSimple className="h-3.5 w-3.5" /> Edit
                      </button>
                      {p.status === 'active' ? (
                        <button onClick={() => statusMutation.mutate({ id: p.id, action: 'archive' })} disabled={statusMutation.isPending} className="inline-flex items-center gap-1 rounded-md border border-plat-border px-2 py-1 text-xs text-plat-secondary hover:bg-plat-hover disabled:cursor-not-allowed disabled:text-plat-disabled">
                          <Archive className="h-3.5 w-3.5" /> Archive
                        </button>
                      ) : (
                        <button onClick={() => statusMutation.mutate({ id: p.id, action: 'activate' })} disabled={statusMutation.isPending} className="inline-flex items-center gap-1 rounded-md border border-plat-success-line px-2 py-1 text-xs text-plat-success-text hover:bg-plat-success-soft disabled:cursor-not-allowed disabled:text-plat-disabled">
                          <ArrowCounterClockwise className="h-3.5 w-3.5" /> Activate
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <div className="flex items-start gap-2.5 rounded-xl border border-plat-border bg-plat-bg px-4 py-3 text-xs text-plat-muted">
        <Tag className="mt-0.5 h-4 w-4 shrink-0 text-plat-faint" />
        <span>These prices drive the contracted revenue (MRR) and the amounts on every invoice. Archiving a plan hides it from new subscriptions but preserves it on existing invoices.</span>
      </div>

      {editing !== null && (
        <PlanModal
          plan={editing === 'new' ? null : editing}
          onClose={() => setEditing(null)}
          onSaved={invalidate}
        />
      )}
    </div>
  );
}
